import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Flag, AlertTriangle } from 'lucide-react';

const REASONS = [
  { v: 'scam', label: 'Scam / Fraud' },
  { v: 'fake_item', label: 'Counterfeit / Fake item' },
  { v: 'wrong_description', label: 'Misleading description' },
  { v: 'inappropriate', label: 'Inappropriate content' },
  { v: 'spam', label: 'Spam' },
  { v: 'stolen_goods', label: 'Stolen goods' },
  { v: 'duplicate_listing', label: 'Duplicate listing' },
  { v: 'other', label: 'Other (please describe)' },
];

/**
 * Report dialog for any listing or user.
 *
 * Props: open, onOpenChange, targetType ('hosted_game'|'auction'|'user'), targetId, targetName
 */
export function ReportDialog({ open, onOpenChange, targetType, targetId, targetName }) {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) { toast.error('Pick a reason'); return; }
    if (reason === 'other' && !description.trim()) { toast.error('Description required for "Other"'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reports`, {
        target_type: targetType, target_id: targetId,
        reason, description,
      });
      toast.success('Report submitted. Admin will review.');
      setReason(''); setDescription('');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit report');
    } finally { setSubmitting(false); }
  };

  const handleOpenChange = (v) => {
    if (!v) { setReason(''); setDescription(''); }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="report-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4">
        <DialogHeader>
          <DialogTitle className="text-xl text-white flex items-center gap-2">
            <Flag className="w-5 h-5 text-red-400" /> Report
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Reporting: <span className="text-white">{targetName || '...'}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="report-reason-select" className="bg-slate-800 border-slate-700 text-white h-10 text-sm">
              <SelectValue placeholder="Pick a reason..." />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {REASONS.map(r => (
                <SelectItem key={r.v} value={r.v} className="text-white">{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            data-testid="report-description-input"
            placeholder={reason === 'other' ? 'Required: describe the issue' : 'Optional: more details'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
          />
          <div className="bg-amber-500/10 border border-amber-500/25 rounded-lg p-2 flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-amber-300 leading-snug">
              False reports may impact your account standing. Admins review every report.
            </p>
          </div>
          <Button data-testid="report-submit-btn" onClick={handleSubmit} disabled={submitting || !reason} className="w-full bg-red-600 hover:bg-red-700 h-10 font-semibold">
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ReportDialog;
